import React, { useState, useRef, useEffect } from "react";
import { PlaySound } from "~/SoundUtils";
import { useAtom } from "jotai";
import { volumeAtom } from "~/Atom";
import "~/css/Accordion.css"; // 基本レイアウト
import "~/css/ConversationAccordion.css"; // 入れ子専用スタイル
import { t } from "i18next";

// 値として参照できるアクション定数
const ButtonAction = {
  hidden: "hidden",
  more: "more",
  close: "close",
} as const;
type ButtonActionType = (typeof ButtonAction)[keyof typeof ButtonAction];

// 入れ子用: アニメーションの時間定数
const transitionDuration = "0.35s";
const transitionTiming = "ease";

// アコーディオンのサイズ
const closeAccordionSize = 445;
const openAccordionSize = 758;

interface ConversationAccordionItemProps {
  title: string | React.JSX.Element;
  children: React.ReactNode;
  dynamicHeight?: boolean;
  summaryClassName?: string;
  defaultOpen?: boolean;
}

/**
 * 入れ子専用アコーディオンアイテム
 */
const ConversationAccordionItem: React.FC<ConversationAccordionItemProps> = ({
  title,
  children,
  summaryClassName = "",
  defaultOpen = false,
}) => {
  const [action, setAction] = useState<ButtonActionType>(ButtonAction.hidden);
  const [height, setHeight] = useState<string>("auto");
  const summaryRef = useRef<HTMLButtonElement>(null); // タイトル（ボタン）
  const contentBodyRef = useRef<HTMLDivElement>(null); // childrenを包む本文
  const [volume] = useAtom(volumeAtom);
  // 計測結果をstateで保持
  const [titleHeight, settitleHeight] = useState(0);
  const [bodyHeight, setbodyHeight] = useState(0);
  const totalHeight = titleHeight + bodyHeight;

  const toggle = () => {
    // action をトグルし、高さを更新
    if (action === ButtonAction.more) {
      setAction(ButtonAction.close);
      setHeight(`${openAccordionSize - titleHeight}px`);
    } else if (action === ButtonAction.close) {
      setAction(ButtonAction.more);
      setHeight(`${closeAccordionSize - titleHeight}px`);
    }
    PlaySound("push", volume);
  };

  // 見出し、本文の高さを設定
  useEffect(() => {
    const measure = () => {
      settitleHeight(summaryRef.current?.offsetHeight ?? 0);
      setbodyHeight(contentBodyRef.current?.scrollHeight ?? 0);
    };
    measure();
    const supportsRO = typeof window !== "undefined" && "ResizeObserver" in window;
    const ro = supportsRO ? new ResizeObserver(() => measure()) : null;
    if (ro) {
      if (summaryRef.current) ro.observe(summaryRef.current);
      if (contentBodyRef.current) ro.observe(contentBodyRef.current);
    }
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [children]);

  // 読み込み初回の状態決定
  useEffect(() => {
    // しきい値以下はボタン非表示＋高さauto
    if (totalHeight < closeAccordionSize) {
      if (action !== ButtonAction.hidden) setAction(ButtonAction.hidden);
      if (height !== "auto") setHeight("auto");
      return;
    }

    if (action === ButtonAction.hidden) {
      const next = defaultOpen ? ButtonAction.close : ButtonAction.more;
      setAction(next);
      setHeight(
        `${(next === ButtonAction.more ? closeAccordionSize : openAccordionSize) - titleHeight}px`
      );
      return;
    }
  }, [totalHeight, titleHeight, defaultOpen, action, height]);

  return (
    <div className={"conversation-accordion-item"}>
      <button
        ref={summaryRef}
        className={`accordion-summary conversation-summary ${summaryClassName}`.trim()}
      >
        {title}
      </button>
      <div
        className="accordion-content conversation-content"
        style={{
          height: height,
          transition: `height ${transitionDuration} ${transitionTiming}`,
        }}
      >
        <div
          className={`content-wrapper ${action === ButtonAction.more ? "not-scroll" : ""}`}
          ref={contentBodyRef}
        >
          {children}
        </div>
      </div>
      {action !== ButtonAction.hidden && (
        <button
          onClick={toggle}
          className={`conversation-button ${action === ButtonAction.more ? "more" : "close"}`}
        >
          {action === ButtonAction.more ? `${t("more")}` : `${t("close")}`}
        </button>
      )}
    </div>
  );
};

export default ConversationAccordionItem;
